import "./style.css";
import { Countdown } from "./timer";
import { formatRemaining, formatTitle } from "./format";
import { alarm, beepFiveMinutes, beepOneMinute, unlock } from "./audio";

const WARN_MS = 5 * 60 * 1000;
const ONE_MIN_MS = 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const STORE_KEY = "countdown.settings.v1";

// --------------------------------------------------------------- elements ---

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const app = $("app");
const faceEl = $("face");
const timeEl = $("time");
const statusEl = $("status");
const controlsEl = $("controls");
const inH = $<HTMLInputElement>("in-h");
const inM = $<HTMLInputElement>("in-m");
const inS = $<HTMLInputElement>("in-s");
const optMillis = $<HTMLInputElement>("opt-millis");
const optHide = $<HTMLInputElement>("opt-hide");
const optSound = $<HTMLInputElement>("opt-sound");
const presetsEl = $("presets");
const btnPrimary = $<HTMLButtonElement>("btn-primary");
const btnReset = $<HTMLButtonElement>("btn-reset");

// --------------------------------------------------------------- settings ---

interface Settings {
  durationMs: number;
  millis: boolean;
  hideControls: boolean;
  sound: boolean;
}

const DEFAULTS: Settings = {
  durationMs: 5 * 60 * 1000,
  millis: false,
  hideControls: true,
  sound: true,
};

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { ...DEFAULTS };
    const saved = JSON.parse(raw) as Partial<Settings>;
    return {
      durationMs:
        typeof saved.durationMs === "number" && saved.durationMs > 0
          ? Math.min(saved.durationMs, 99 * HOUR_MS)
          : DEFAULTS.durationMs,
      millis: saved.millis ?? DEFAULTS.millis,
      hideControls: saved.hideControls ?? DEFAULTS.hideControls,
      sound: saved.sound ?? DEFAULTS.sound,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function saveSettings(): void {
  try {
    const settings: Settings = {
      durationMs: timer.duration,
      millis: optMillis.checked,
      hideControls: optHide.checked,
      sound: optSound.checked,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(settings));
  } catch {
    /* private mode / quota — the app works fine without persistence */
  }
}

// ------------------------------------------------------------------ state ---

const timer = new Countdown();
/** Remaining value at the previous tick, for detecting threshold crossings. */
let previous = 0;
let lastPainted = "";
let lastTitleSecond = -1;
let rafId = 0;

const isTouch = window.matchMedia("(pointer: coarse)").matches;

// ------------------------------------------------------------- wake lock ---

type Sentinel = { release(): Promise<void>; released: boolean };
type WakeLockNavigator = Navigator & {
  wakeLock?: { request(type: "screen"): Promise<Sentinel> };
};

let sentinel: Sentinel | null = null;

async function acquireWakeLock(): Promise<void> {
  const api = (navigator as WakeLockNavigator).wakeLock;
  if (!api || sentinel) return;
  try {
    sentinel = await api.request("screen");
  } catch {
    /* denied, or the tab lost focus mid-request — not worth surfacing */
  }
}

function releaseWakeLock(): void {
  void sentinel?.release().catch(() => {});
  sentinel = null;
}

// ------------------------------------------------------------------- view ---

function durationFromInputs(): number {
  const clamp = (input: HTMLInputElement, max: number): number => {
    const n = Math.floor(Number(input.value));
    return Number.isFinite(n) ? Math.min(Math.max(n, 0), max) : 0;
  };
  return (
    clamp(inH, 99) * HOUR_MS + clamp(inM, 59) * ONE_MIN_MS + clamp(inS, 59) * 1000
  );
}

function writeInputs(ms: number): void {
  const seconds = Math.floor(ms / 1000);
  inH.value = String(Math.floor(seconds / 3600));
  inM.value = String(Math.floor((seconds % 3600) / 60));
  inS.value = String(seconds % 60);
}

function markActivePreset(): void {
  const minutes = timer.duration / ONE_MIN_MS;
  for (const btn of presetsEl.querySelectorAll<HTMLButtonElement>("button")) {
    btn.setAttribute(
      "aria-pressed",
      String(Number(btn.dataset.minutes) === minutes),
    );
  }
}

const CHROME = {
  idle: ["Start", "Press <kbd>Space</kbd> to start", "Tap anywhere to start"],
  running: ["Pause", "<kbd>Space</kbd> to pause", "Tap anywhere to pause"],
  paused: [
    "Resume",
    "Paused · <kbd>Space</kbd> to resume",
    "Paused · tap anywhere to resume",
  ],
  finished: [
    "Restart",
    "Time's up · <kbd>Space</kbd> to restart",
    "Time's up · tap anywhere to restart",
  ],
} as const;

function renderChrome(): void {
  const state = timer.state;
  // Every transition changes what the tab title should say, even when the
  // whole second has not ticked over yet — force the next paint to rewrite it.
  lastTitleSecond = -1;
  app.dataset.state = state;
  app.dataset.chrome =
    state === "running" && optHide.checked ? "hidden" : "shown";

  const [label, keyHint, tapHint] = CHROME[state];
  btnPrimary.textContent = label;
  statusEl.innerHTML = isTouch ? tapHint : keyHint;

  btnPrimary.disabled = timer.duration <= 0;

  // Inputs are only meaningful before the clock is running.
  const locked = state === "running";
  for (const input of [inH, inM, inS]) input.disabled = locked;
}

/**
 * Size the digits to the viewport by measuring them, rather than guessing an
 * average glyph advance in CSS. Only runs when the *shape* of the string
 * changes (mm:ss → hh:mm:ss.mmm) or on resize, so the forced reflow is rare.
 */
let fittedChars = -1;

function fitTime(): void {
  const probePx = 100;
  timeEl.style.fontSize = `${probePx}px`;
  const width = timeEl.getBoundingClientRect().width;
  if (width <= 0) return;

  // The face's own box, not the viewport: while the panel is on screen the
  // digits have less room, and its height is unaffected by the recentring
  // transform, so this stays stable across start/pause.
  const room = faceEl.getBoundingClientRect().height;
  const size = Math.min(
    (window.innerWidth * 0.94 * probePx) / width,
    room * 0.78,
    window.innerHeight * 0.5,
  );
  timeEl.style.fontSize = `${Math.floor(size)}px`;
}

function measurePanel(): void {
  const height = controlsEl.getBoundingClientRect().height;
  document.documentElement.style.setProperty("--panel", `${height}px`);
}

function paint(remaining: number): void {
  const withHours = timer.duration >= HOUR_MS;
  const { text, chars } = formatRemaining(
    remaining,
    withHours,
    optMillis.checked,
  );

  if (text !== lastPainted) {
    timeEl.textContent = text;
    lastPainted = text;
    if (chars !== fittedChars) {
      fittedChars = chars;
      fitTime();
    }
  }

  const warn = timer.state !== "idle" && remaining <= WARN_MS;
  app.dataset.warn = String(warn);

  const second = Math.ceil(remaining / 1000);
  if (second !== lastTitleSecond) {
    lastTitleSecond = second;
    document.title =
      timer.state === "running"
        ? `${formatTitle(remaining, withHours)} · Countdown`
        : "Countdown";
  }
}

// ------------------------------------------------------------------- loop ---

/**
 * Idempotent — safe to call from rAF and from the interval in the same frame.
 * rAF gives smooth millisecond digits while visible; the interval keeps the
 * beeps roughly on time when the tab is backgrounded and rAF stops firing.
 */
function tick(): void {
  const now = performance.now();
  const remaining = timer.remaining(now);

  if (timer.state === "running") {
    if (optSound.checked) {
      if (previous > WARN_MS && remaining <= WARN_MS) beepFiveMinutes();
      if (previous > ONE_MIN_MS && remaining <= ONE_MIN_MS) beepOneMinute();
    }
    previous = remaining;

    if (remaining <= 0) {
      timer.finish();
      if (optSound.checked) alarm();
      releaseWakeLock();
      stopFrames();
      renderChrome();
    }
  }

  paint(timer.remaining(performance.now()));
}

/**
 * Frame-rate painting is only needed while the clock moves. Left running on an
 * idle phone it is pure battery drain, and the 250 ms interval below already
 * covers every state where nothing is counting down.
 */
function frame(): void {
  rafId = requestAnimationFrame(frame);
  tick();
}

function startFrames(): void {
  if (!rafId) rafId = requestAnimationFrame(frame);
}

function stopFrames(): void {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
}

// ---------------------------------------------------------------- actions ---

function applyDuration(ms: number): void {
  if (timer.state === "running") return;
  timer.setDuration(ms);
  previous = timer.duration;
  markActivePreset();
  renderChrome();
  tick();
  saveSettings();
}

function start(): void {
  if (timer.duration <= 0) return;
  if (timer.state === "finished") timer.reset();
  if (timer.state === "idle") previous = timer.duration;

  if (optSound.checked) unlock();
  timer.start(performance.now());
  void acquireWakeLock();
  startFrames();

  (document.activeElement as HTMLElement | null)?.blur?.();
  renderChrome();
  tick();
}

function pause(): void {
  timer.pause(performance.now());
  releaseWakeLock();
  stopFrames();
  renderChrome();
  tick();
}

/**
 * Pointer toggles are debounced; keyboard toggles are not.
 *
 * Pausing un-hides the control panel, so without a guard a tap near the bottom
 * of the screen pauses on `pointerdown` and then lands its `click` on the
 * Resume button that just appeared underneath it. The keyboard has no such
 * double-delivery problem, and Space must never feel like it dropped a press.
 */
let pointerReadyAt = 0;

function toggle(viaPointer = false): void {
  if (viaPointer) {
    const now = performance.now();
    if (now < pointerReadyAt) return;
    pointerReadyAt = now + 350;
  }

  switch (timer.state) {
    case "running":
      pause();
      break;
    case "finished":
      reset();
      start();
      break;
    default:
      start();
  }
}

function reset(): void {
  timer.reset();
  previous = timer.duration;
  releaseWakeLock();
  stopFrames();
  renderChrome();
  tick();
}

// ----------------------------------------------------------------- events ---

// Space is captured globally — starting the timer is the app's whole job, and
// it must work no matter what happens to hold focus. That does mean Space will
// not toggle a focused checkbox; click or tap those instead.
window.addEventListener(
  "keydown",
  (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;

    const key = event.key;
    const typing =
      document.activeElement instanceof HTMLInputElement &&
      document.activeElement.type === "number";

    if (key === " " || key === "Spacebar") {
      event.preventDefault();
      if (typing) {
        (document.activeElement as HTMLInputElement).blur();
        applyDuration(durationFromInputs());
      }
      toggle();
      return;
    }

    if (typing) return;

    if (key === "r" || key === "R" || key === "Escape") {
      event.preventDefault();
      reset();
    } else if (key === "f" || key === "F") {
      event.preventDefault();
      void (document.fullscreenElement
        ? document.exitFullscreen()
        : document.documentElement.requestFullscreen()
      ).catch(() => {});
    }
  },
  { passive: false },
);

// Tap anywhere outside the control panel to start/pause — the mobile equivalent
// of Space. While the chrome is hidden the panel is pointer-events:none, so the
// whole screen is a target.
document.addEventListener("pointerdown", (event) => {
  if (event.button !== 0) return; // ignore right/middle click
  const target = event.target as Node;
  if (controlsEl.contains(target) && app.dataset.chrome !== "hidden") return;
  toggle(true);
});

for (const input of [inH, inM, inS]) {
  input.addEventListener("input", () => applyDuration(durationFromInputs()));
  input.addEventListener("blur", () => writeInputs(timer.duration));
}

presetsEl.addEventListener("click", (event) => {
  const btn = (event.target as HTMLElement).closest<HTMLButtonElement>("button");
  const minutes = Number(btn?.dataset.minutes);
  if (!btn || !Number.isFinite(minutes)) return;
  applyDuration(minutes * ONE_MIN_MS);
  writeInputs(timer.duration);
});

optMillis.addEventListener("change", () => {
  lastPainted = "";
  saveSettings();
  tick();
});

optHide.addEventListener("change", () => {
  saveSettings();
  renderChrome();
});

optSound.addEventListener("change", () => {
  if (optSound.checked) unlock();
  saveSettings();
});

btnPrimary.addEventListener("click", () => toggle(true));
btnReset.addEventListener("click", () => reset());

function relayout(): void {
  measurePanel();
  fitTime();
}

window.addEventListener("resize", relayout);

// The panel also changes height on its own — the preset row rewraps, the hint
// line drops out below 34rem — which moves both the recentring offset and the
// vertical room the digits get.
new ResizeObserver(relayout).observe(controlsEl);

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    if (timer.state === "running") {
      void acquireWakeLock();
      startFrames();
    }
    tick();
  }
});

// -------------------------------------------------------------------- boot ---

const settings = loadSettings();
optMillis.checked = settings.millis;
optHide.checked = settings.hideControls;
optSound.checked = settings.sound;
writeInputs(settings.durationMs);
applyDuration(settings.durationMs);
relayout();

// A coarse backup clock: rAF is suspended in a background tab, this is only
// throttled, so the 5-minute and 1-minute beeps still land within ~1s. It also
// covers idle and paused states, where no frame loop is running at all.
window.setInterval(tick, 250);

window.addEventListener("pagehide", () => {
  stopFrames();
  releaseWakeLock();
});
